import { isEqual } from 'lodash'
import { action, computed, makeAutoObservable, observable } from 'mobx'

import { WEB_LINK_REGEXP } from 'shared/constants/regExp'

import { CCTVInfoDTO, ValidateCCTVInfoDTO, VendorList } from '../../../types'
import { OrderCCTVApiStore } from './OrderCCTVApiStore'
import { OrderCCTVInitialStateType } from './types'

export class OrderCCTVModel {
  @observable vendorList: VendorList[] = []
  @observable vendorValue: number | null = null

  @observable url = ''
  @observable login = ''
  @observable password = ''
  @observable token = ''
  @observable description = ''

  @observable validateCCTVResult: ValidateCCTVInfoDTO | null = null
  @observable showCCTVModal = false
  @observable initializedCCTVInfo = false

  private initialState: OrderCCTVInitialStateType | null = null

  readonly apiStore = new OrderCCTVApiStore(this)

  constructor() {
    makeAutoObservable(this)
  }

  get isNoChanges() {
    return isEqual(this.initialState, this.hashFormState)
  }

  @computed
  get invalidUrl() {
    return this.url && !WEB_LINK_REGEXP.test(this.url)
  }

  @computed
  get hasToken() {
    return this.vendorList.find((vendor) => vendor.id === this.vendorValue)?.hasToken
  }

  get hashFormState(): OrderCCTVInitialStateType {
    return {
      vendorValue: this.vendorValue,
      url: this.url.trim(),
      login: this.login.trim(),
      password: this.password.trim(),
      token: this.token.trim(),
      description: this.description.trim(),
    }
  }

  initCCTVInfo = async (value: CCTVInfoDTO) => {
    this.setUrl(value.url)
    this.setLogin(value.login)
    this.setPassword(value.password)
    this.setDescription(value.description)
    this.setInitialState(this.hashFormState)
    this.setInitializedCCTVInfo(true)
  }

  @action
  setInitialState = (value: OrderCCTVInitialStateType | null) => {
    this.initialState = value
  }

  @action
  setVendorList = (value: VendorList[]) => {
    this.vendorList = value
  }

  @action
  setVendorValue = (value: number | null) => {
    this.vendorValue = value
  }

  @action
  setUrl = (value: string) => {
    this.url = value
  }

  @action
  setLogin = (value: string) => {
    this.login = value
  }

  @action
  setPassword = (value: string) => {
    this.password = value
  }

  @action
  setToken = (value: string) => {
    this.token = value
  }

  @action
  setDescription = (value: string) => {
    this.description = value
  }

  @action
  setValidateCCTVResult = (value: ValidateCCTVInfoDTO) => {
    this.validateCCTVResult = value
  }

  @action
  setShowCCTVModal = (value: boolean) => {
    this.showCCTVModal = value
  }

  @action
  setInitializedCCTVInfo = (value: boolean) => {
    this.initializedCCTVInfo = value
  }

  @action
  resetCCTVInfo = () => {
    this.setVendorValue(null)
    this.setUrl('')
    this.setLogin('')
    this.setPassword('')
    this.setToken('')
    this.setDescription('')
    this.setInitializedCCTVInfo(false)
  }

  async getVendorList() {
    return await this.apiStore.getVendorList()
  }

  async validateCCTVInfo(pvzId: number) {
    return await this.apiStore.validateCCTVInfo(pvzId)
  }

  async addCCTVInfo(orderId: number) {
    return await this.apiStore.addCCTVInfo(orderId)
  }

  async getCCTVInfo(orderId: number) {
    return await this.apiStore.getCCTVInfo(orderId)
  }
}
